
import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Assessment, VersionHistoryEntry } from '@/types/assessment';
import { formatDistanceToNow } from 'date-fns';
import { Check, FileCheck, Archive } from 'lucide-react';

interface VersionHistoryDialogProps {
  assessment: Assessment;
  isOpen: boolean;
  onClose: () => void;
  onRevertToVersion: (versionNumber: number) => Promise<void>;
  loading: boolean;
}

export function VersionHistoryDialog({
  assessment,
  isOpen,
  onClose,
  onRevertToVersion,
  loading
}: VersionHistoryDialogProps) {
  if (!assessment.versionHistory || assessment.versionHistory.length === 0) {
    return null;
  }

  // Sort versions in descending order (newest first)
  const sortedVersions = [...assessment.versionHistory]
    .sort((a, b) => b.versionNumber - a.versionNumber);
  
  // Add current version to the list
  const allVersions = [
    {
      versionNumber: assessment.version,
      timestamp: assessment.updatedAt,
      isCurrent: true,
      categories: assessment.categories
    },
    ...sortedVersions.map(v => ({ ...v, isCurrent: false }))
  ];
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Version History</DialogTitle>
          <DialogDescription>
            View previous versions of this assessment. You can revert to a previous version if needed.
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[300px] mt-4 pr-4">
          <div className="space-y-4">
            {allVersions.map((version, index) => (
              <div 
                key={version.versionNumber} 
                className={`p-4 border rounded-md ${version.isCurrent ? 'border-primary bg-primary/5' : 'border-border'}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium flex items-center">
                      Version {version.versionNumber}
                      {version.isCurrent && (
                        <span className="ml-2 text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(version.timestamp), { addSuffix: true })}
                    </div>
                  </div>
                  
                  {!version.isCurrent && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={loading} 
                      onClick={() => onRevertToVersion(version.versionNumber)}
                    >
                      <FileCheck className="h-4 w-4 mr-1" />
                      Revert to this version
                    </Button>
                  )}
                </div>
                
                {version.categories && (
                  <div className="text-sm mt-2">
                    <span className="text-muted-foreground">{version.categories.length} categories, </span>
                    <span className="text-muted-foreground">
                      {version.categories.reduce((sum, cat) => sum + cat.questions.length, 0)} questions
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
